import type {
    ActionDocument,
    PackActionProposal,
    DocumentPackSectionWriteRequest,
} from '@quipex/shared/contracts';
import { isCanonicalDocumentId } from '../source-document-id';
import { groupPackDocuments } from './grouping';
import type { QuippyPackCommand } from './models';

export function buildPackCommand(input: {
    proposal: PackActionProposal;
    documents: readonly ActionDocument[];
    name: string;
    rowNames?: ReadonlyMap<string, string>;
    sectionNames?: ReadonlyMap<string, string>;
    defaultSection?: string;
    requirementIds?: ReadonlyMap<string, string>;
    description?: string;
    sections?: DocumentPackSectionWriteRequest[];
    rowDescriptions?: ReadonlyMap<string, string>;
}): QuippyPackCommand {
    const name = input.name.trim();
    if (input.proposal.kind !== 'createPack')
        throw new Error(
            'Only new packs can be created here. Manage existing packs in Packs.'
        );
    if (!name || name.length > 100)
        throw new Error('Enter a pack name of at most 100 characters.');
    if (
        !isCanonicalDocumentId(input.proposal.buildingId) ||
        (!input.sections && input.documents.length < 1) ||
        input.documents.length > 50
    )
        throw new Error('Select between 1 and 50 documents from one building.');
    if (
        input.documents.some(
            (document) =>
                document.buildingId !== input.proposal.buildingId ||
                !isCanonicalDocumentId(document.documentId)
        )
    )
        throw new Error('Each document must belong to this building.');
    if (
        (input.description?.length ?? 0) > 512 ||
        [...(input.rowDescriptions?.values() ?? [])].some(
            (value) => value.length > 512
        )
    )
        throw new Error('Descriptions must be 512 characters or fewer.');
    const sections =
        input.sections?.map((section) => ({
            name: section.name.trim(),
            requirements: section.requirements.map((row) => ({
                name: row.name,
                description: row.description,
                documentIds: row.assignedDocumentIds ?? [],
            })),
        })) ??
        groupPackDocuments(
            input.documents,
            input.rowNames ?? new Map(),
            input.sectionNames ?? new Map(),
            input.defaultSection ?? input.proposal.sectionName,
            input.requirementIds
        );
    if (
        !sections.length ||
        !sections.some((section) => section.requirements.length)
    )
        throw new Error('Add at least one requirement.');
    if (sections.some((section) => !section.name))
        throw new Error('Enter a section name for each document.');
    const assigned = sections.flatMap((section) =>
        section.requirements.flatMap((row) => row.documentIds)
    );
    const sourceIds = new Set(
        input.documents.map((document) => document.documentId)
    );
    if (
        new Set(assigned).size !== assigned.length ||
        assigned.length !== sourceIds.size ||
        assigned.some((id) => !sourceIds.has(id))
    )
        throw new Error(
            'Each document must be assigned once within this building.'
        );
    if (
        sections.some((section) =>
            section.requirements.some((row) => row.documentIds.length > 5)
        )
    )
        throw new Error('Each requirement can have at most five documents.');
    if (
        sections.some((section) =>
            section.requirements.some(
                (row) => (row.description?.length ?? 0) > 512
            )
        )
    )
        throw new Error('Descriptions must be 512 characters or fewer.');
    if (
        sections.some(
            (section) =>
                section.name.length > 200 ||
                section.requirements.some(
                    (row) => !row.name.trim() || row.name.trim().length > 200
                )
        )
    )
        throw new Error(
            'Enter section and requirement names of at most 200 characters.'
        );
    return {
        buildingId: input.proposal.buildingId,
        name,
        ...(input.description?.trim()
            ? { description: input.description.trim() }
            : {}),
        sections: sections.map((section) => ({
            name: section.name,
            requirements: section.requirements.map((row) => ({
                name: row.name.trim(),
                ...((
                    row.description ??
                    input.rowDescriptions?.get(row.documentIds[0])
                )?.trim()
                    ? {
                          description: (
                              row.description ??
                              input.rowDescriptions?.get(row.documentIds[0])
                          )?.trim(),
                      }
                    : {}),
                assignedDocumentIds: row.documentIds,
            })),
        })),
    };
}
